import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import api, { API } from "@/lib/api";
import { getDeviceLocationSnapshot, deviceLocationLines } from "@/lib/deviceLocation";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Video, Upload, Circle, Square, Mic, Trash2, Download, ShieldCheck,
  X, Loader2, RotateCw, AlertTriangle, MapPin,
} from "lucide-react";

const MAX_BYTES = 75 * 1024 * 1024;
const MAX_SECONDS = 60;
const REC_MIME_CANDIDATES = [
  "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm",
];

function pickRecorderMime() {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  for (const m of REC_MIME_CANDIDATES) {
    try { if (window.MediaRecorder.isTypeSupported(m)) return m; } catch { /* noop */ }
  }
  return null;
}

const fmtSize = (b) => (b == null ? "—" : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const fmtDur = (s) => (s == null ? null : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

const token = () => localStorage.getItem("dd_token");

export function RoomWalkthroughVideo({ propertyId, roomId, locked, archived }) {
  const readOnly = Boolean(locked || archived);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("idle"); // idle | review | recording | uploading
  const [cand, setCand] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploadErr, setUploadErr] = useState(false);
  const [narration, setNarration] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const fileRef = useRef(null);
  const uploadIdRef = useRef(null);
  const abortRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const recMime = useRef(pickRecorderMime());
  const recStartRef = useRef(null);
  const locSnapshotRef = useRef(null); // Feature 19: one frozen snapshot per video workflow (null = not yet collected)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rooms/${roomId}/video`);
      setVideo(data.video || null);
    } catch { setVideo(null); }
    setLoading(false);
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  const stopStream = useCallback(() => {
    try { (streamRef.current?.getTracks() || []).forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
  }, []);

  const clearCand = useCallback(() => {
    if (cand?.url) { try { URL.revokeObjectURL(cand.url); } catch { /* noop */ } }
    setCand(null); setProgress(0); setUploadErr(false); uploadIdRef.current = null;
    locSnapshotRef.current = null; // new video workflow collects its own fresh snapshot
  }, [cand]);

  useEffect(() => () => { stopStream(); if (timerRef.current) clearInterval(timerRef.current); }, [stopStream]);

  // ---- native selection ----
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error(`Video is too large (max ${MAX_BYTES / 1024 / 1024} MB).`); return; }
    const url = URL.createObjectURL(file);
    let duration = null;
    try {
      duration = await new Promise((res) => {
        const vv = document.createElement("video");
        vv.preload = "metadata";
        vv.onloadedmetadata = () => res(Number.isFinite(vv.duration) ? vv.duration : null);
        vv.onerror = () => res(null);
        vv.src = url;
      });
    } catch { duration = null; }
    setCand({
      file, url, name: file.name, size: file.size,
      duration, durationSource: duration != null ? "browser_metadata" : "unknown",
      hasAudio: null, audioSource: "unknown",
      sourceType: "file_selected",
      selectedAt: new Date().toISOString(),
      lastModified: file.lastModified ? new Date(file.lastModified).toISOString() : null,
      capturedAt: null,
    });
    setStage("review");
  };

  // ---- in-app recording ----
  const startRecording = async () => {
    const mime = recMime.current;
    if (!mime) { toast.error("In-app recording isn't supported on this browser."); return; }
    if (narration) toast("This will record audio with the walkthrough video.");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: narration });
    } catch (err) {
      if (narration) {
        // Microphone denied/unavailable — fall back to video-only.
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
          toast("Microphone unavailable — recording video only.");
        } catch { toast.error("Camera unavailable."); return; }
      } else { toast.error("Camera unavailable."); return; }
    }
    const gotAudio = stream.getAudioTracks().length > 0;
    streamRef.current = stream;
    chunksRef.current = [];
    const rec = new MediaRecorder(stream, { mimeType: mime });
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      const blob = new Blob(chunksRef.current, { type: mime });
      const ext = mime.startsWith("video/mp4") ? "mp4" : "webm";
      const file = new File([blob], `walkthrough-${Date.now()}.${ext}`, { type: blob.type });
      const dur = recStartRef.current ? (Date.now() - recStartRef.current) / 1000 : null;
      stopStream();
      setStage("review");
      setCand({
        file, url: URL.createObjectURL(blob), name: file.name, size: file.size,
        duration: dur, durationSource: dur != null ? "tenantseal_recorder" : "unknown",
        hasAudio: gotAudio, audioSource: "tenantseal_recorder",
        sourceType: "tenantseal_recorder",
        capturedAt: recStartRef.current ? new Date(recStartRef.current).toISOString() : new Date().toISOString(),
        selectedAt: null, lastModified: null,
      });
    };
    recRef.current = rec;
    recStartRef.current = Date.now();
    setElapsed(0);
    rec.start();
    setStage("recording");
    timerRef.current = setInterval(() => {
      const s = (Date.now() - recStartRef.current) / 1000;
      setElapsed(s);
      if (s >= MAX_SECONDS) stopRecording();
    }, 250);
  };

  const stopRecording = () => { try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch { /* noop */ } };

  const cancelRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    try { recRef.current && recRef.current.state !== "inactive" && (recRef.current.onstop = null, recRef.current.stop()); } catch { /* noop */ }
    stopStream(); setStage("idle");
  };

  // ---- upload (progress + cancel + retry with same client_upload_id) ----
  const doUpload = async () => {
    if (!cand) return;
    if (!uploadIdRef.current) uploadIdRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setStage("uploading"); setUploadErr(false); setProgress(0);
    const fd = new FormData();
    fd.append("file", cand.file, cand.name);
    fd.append("client_upload_id", uploadIdRef.current);
    fd.append("source_type", cand.sourceType);
    if (cand.capturedAt) fd.append("client_captured_at", cand.capturedAt);
    if (cand.selectedAt) fd.append("client_selected_at", cand.selectedAt);
    if (cand.lastModified) fd.append("file_last_modified_at", cand.lastModified);
    if (cand.duration != null) { fd.append("duration_seconds", cand.duration); fd.append("duration_source", cand.durationSource); }
    if (cand.hasAudio != null) { fd.append("has_audio", cand.hasAudio); fd.append("audio_source", cand.audioSource); }
    // Feature 19: freeze ONE fresh device-location snapshot for this video workflow on the first
    // attempt, then reuse it verbatim on every retry (never recollect during upload/retry).
    if (locSnapshotRef.current === null) {
      locSnapshotRef.current = (await getDeviceLocationSnapshot()) || false;
    }
    const dl = locSnapshotRef.current || null;
    if (dl) fd.append("device_location", JSON.stringify(dl));
    abortRef.current = new AbortController();
    try {
      const { data } = await axios.post(`${API}/rooms/${roomId}/videos`, fd, {
        headers: { Authorization: `Bearer ${token()}` },
        signal: abortRef.current.signal,
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setVideo(data.video);
      clearCand();
      setStage("idle");
      toast.success("Walkthrough video uploaded — fingerprint recorded at upload.");
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === "ERR_CANCELED") { setStage("review"); return; }
      setUploadErr(true); setStage("review");
      const d = err?.response?.data?.detail;
      toast.error(d === "room_video_exists" ? "This room already has a walkthrough video." :
        d === "video_too_large" ? "Video is too large." :
        d === "unsupported_video_container" ? "That video format isn't supported." :
        "Upload failed — your video is kept here. Leaving this page may require selecting or recording it again.");
    }
  };

  const cancelUpload = () => { try { abortRef.current?.abort(); } catch { /* noop */ } };

  const deleteVideo = async () => {
    if (!video) return;
    try { await api.delete(`/videos/${video.video_id}`); setVideo(null); toast.success("Video deleted."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete the video."); }
  };

  const streamUrl = video ? `${API}/videos/${video.video_id}/stream` : "";
  const downloadUrl = video ? `${API}/videos/${video.video_id}/download` : "";

  if (loading) return null;

  return (
    <div className="max-w-3xl mx-auto mt-5" data-testid="room-video-section">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex items-center gap-2 text-white/90 font-semibold mb-3">
          <Video size={18} className="text-amber" /> Walkthrough video
          <span className="ml-auto text-[11px] font-normal text-white/40">Optional · one per room</span>
        </div>

        {/* PRESENT */}
        {video && stage === "idle" && (
          <div data-testid="room-video-present" className="space-y-3">
            <video data-testid="room-video-player" src={streamUrl} controls playsInline
              className="w-full max-h-[46vh] rounded-xl bg-black" />
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60">
              <span>{fmtSize(video.size_bytes)}</span>
              {fmtDur(video.duration_seconds) && <span>{fmtDur(video.duration_seconds)} · {video.duration_source}</span>}
              <span className="uppercase">{video.container}</span>
              {video.has_audio === false && <span>no audio</span>}
            </div>
            <div className="rounded-lg bg-white/[0.04] border border-white/10 p-2.5 text-[11px] text-white/55" data-testid="room-video-fingerprint">
              <div className="flex items-center gap-1.5 text-emerald-300 font-medium">
                <ShieldCheck size={13} /> {video.sealed ? "Included in this property’s seal" : "Tamper-evident after upload"}
              </div>
              <div className="mt-1 font-mono break-all">SHA-256 {String(video.sha256 || "").slice(0, 24)}…</div>
              <div className="mt-1">Fingerprint recorded at upload. TenantSeal records a fingerprint of the uploaded video. This can show whether those bytes changed after upload, but it does not verify what happened before the video reached TenantSeal.</div>
            </div>
            {video.device_location && (
              <div className="rounded-lg bg-white/[0.04] border border-white/10 p-2.5 text-[11px] text-white/55" data-testid="room-video-device-location">
                <div className="flex items-center gap-1.5 text-white/70 font-medium">
                  <MapPin size={13} /> {deviceLocationLines(video.device_location)[0]}
                </div>
                {deviceLocationLines(video.device_location).slice(1).map((line, i) => (
                  <div key={i} className="mt-1">{line}</div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <a data-testid="room-video-download" href={downloadUrl}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm">
                <Download size={15} /> Download original
              </a>
              {!readOnly && (
                <Button data-testid="room-video-delete" onClick={deleteVideo} variant="outline"
                  className="h-10 rounded-xl border-white/15 bg-transparent text-white/80 text-sm">
                  <Trash2 size={15} className="mr-1.5" /> Delete
                </Button>
              )}
              {video.sealed && <span className="ml-auto self-center text-[11px] text-emerald-300">Sealed · read-only</span>}
            </div>
          </div>
        )}

        {/* EMPTY */}
        {!video && stage === "idle" && (
          <div data-testid="room-video-empty">
            {readOnly ? (
              <p className="text-sm text-white/50">No walkthrough video for this room.</p>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input ref={fileRef} type="file" accept="video/*" capture="environment" onChange={onFilePicked} className="hidden" data-testid="room-video-file-input" />
                <Button data-testid="room-video-add" onClick={() => fileRef.current?.click()}
                  className="h-11 rounded-xl bg-amber hover:bg-amber-hover text-navy font-semibold">
                  <Upload size={16} className="mr-1.5" /> Add walkthrough video
                </Button>
                {recMime.current && (
                  <Button data-testid="room-video-record" onClick={startRecording} variant="outline"
                    className="h-11 rounded-xl border-white/15 bg-transparent text-white">
                    <Circle size={14} className="mr-1.5 text-red-400 fill-red-400" /> Record in TenantSeal
                  </Button>
                )}
              </div>
            )}
            {!readOnly && (
              <p className="mt-2 text-[11px] text-white/40">Max 60s · max 75 MB · one video per room. Videos recorded by your device may contain audio. Review the video before uploading.</p>
            )}
          </div>
        )}

        {/* RECORDING */}
        {stage === "recording" && (
          <div data-testid="room-video-recording" className="text-center py-6">
            <div className="text-3xl font-mono text-white" data-testid="room-video-timer">{fmtDur(elapsed)} <span className="text-sm text-white/40">/ 1:00</span></div>
            <p className="text-xs text-white/50 mt-1">{narration ? "Recording with narration" : "Recording video only"} · stops automatically at 60s</p>
            <Button data-testid="room-video-stop" onClick={stopRecording} className="mt-4 h-12 rounded-xl bg-red-500 hover:bg-red-600 text-white">
              <Square size={16} className="mr-1.5" /> Stop
            </Button>
            <button data-testid="room-video-cancel-rec" onClick={cancelRecording} className="block mx-auto mt-2 text-xs text-white/50">Cancel</button>
          </div>
        )}

        {/* REVIEW */}
        {stage === "review" && cand && (
          <div data-testid="room-video-review" className="space-y-3">
            <video src={cand.url} controls playsInline muted className="w-full max-h-[42vh] rounded-xl bg-black" data-testid="room-video-review-preview" />
            <p className="text-[11px] text-white/50" data-testid="room-video-preview-note">If this browser cannot preview the original video format, you can still upload and download the original.</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/70">
              <span className="truncate max-w-[60%]">{cand.name}</span>
              <span>{fmtSize(cand.size)}</span>
              {fmtDur(cand.duration) && <span>{fmtDur(cand.duration)} · {cand.durationSource}</span>}
              {cand.sourceType === "tenantseal_recorder"
                ? <span>{cand.hasAudio ? "with narration" : "no audio"} · captured by TenantSeal</span>
                : <span>selected file · may contain audio</span>}
            </div>
            {uploadErr && (
              <div className="rounded-lg bg-red-500/10 border border-red-400/30 p-2 text-xs text-red-200 flex items-start gap-1.5" data-testid="room-video-upload-error">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> Upload failed — your video is kept here. Leaving this page may require selecting or recording it again.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="room-video-reselect" onClick={() => { clearCand(); setStage("idle"); }} variant="outline" className="h-11 rounded-xl border-white/15 bg-transparent text-white">
                <RotateCw size={15} className="mr-1.5" /> {cand.sourceType === "tenantseal_recorder" ? "Retake" : "Reselect"}
              </Button>
              <Button data-testid="room-video-use" onClick={doUpload} className="h-11 rounded-xl bg-amber hover:bg-amber-hover text-navy font-semibold">
                {uploadErr ? "Retry upload" : "Use video"}
              </Button>
            </div>
          </div>
        )}

        {/* UPLOADING */}
        {stage === "uploading" && (
          <div data-testid="room-video-uploading" className="space-y-3 py-3">
            <div className="flex items-center gap-2 text-white/80 text-sm"><Loader2 size={15} className="animate-spin" /> Uploading… {progress}% · {fmtSize(cand?.size)}</div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-amber transition-all" style={{ width: `${progress}%` }} data-testid="room-video-progress" /></div>
            <Button data-testid="room-video-cancel-upload" onClick={cancelUpload} variant="outline" className="h-9 rounded-xl border-white/15 bg-transparent text-white/80 text-sm">
              <X size={14} className="mr-1.5" /> Cancel
            </Button>
          </div>
        )}

        {/* narration opt-in (only when recorder available and empty) */}
        {!video && stage === "idle" && recMime.current && !readOnly && (
          <label className="mt-3 flex items-center gap-2 text-xs text-white/60 cursor-pointer" data-testid="room-video-narration-toggle">
            <input type="checkbox" checked={narration} onChange={(e) => setNarration(e.target.checked)} />
            <Mic size={13} /> Include narration (records audio — asks for microphone permission)
          </label>
        )}
      </div>
    </div>
  );
}
